import {TreeViewItem} from '../@types/tree-view-item';
import {getPathToRootAsArrayOfString, makeGroup, role} from './group-utils';
import {BreadCrumbsProp} from '../@types/bread-crumb';
import {getResourceNameWithAscendants} from './resource-utils';
import {ResourceType} from '../@types/resource-type';
import PsfTemplate, {PsfCategory} from '../@types/psfTemplate';
import Group from '../@types/group';
import {hasRightToEdit} from './role-to-rights';

export function psfTemplateTreeItems(psfTemplates: PsfTemplate[]): TreeViewItem[] {
    const obj = psfTemplates.map((psfTemplate: PsfTemplate) => {
        return {...psfTemplate, breadcrumb: getPathToRootAsArrayOfString(psfTemplate.group)}
    }).reduce((acc: any, psft: PsfTemplate) => {
        const path = psft.breadcrumb.map((bread: BreadCrumbsProp) => bread.label).join('>') + '-' + psft.group.id;
        if (!acc[path]) {
            acc[path] = [];
        }
        acc[path].push(psft);
        return acc;
    }, {});

    Object.keys(obj).forEach((key) => {
        obj[key].sort((a: PsfTemplate, b: PsfTemplate) => {
            return b.name > a.name;
        });
    });
    const treeItems: TreeViewItem[] = [];
    Object.keys(obj).sort().forEach((key) => {
        const fpsft = obj[key][0];
        const psft_group = fpsft.group;

        treeItems.push(...obj[key].map((sm: PsfTemplate) => psfTemplateTreeItem(sm, role(psft_group),
            getResourceNameWithAscendants(psft_group, ResourceType.GROUPS))));
    });


    return treeItems.sort((a: TreeViewItem, b: TreeViewItem) => a.name.localeCompare(b.name) || a.groupPath!.localeCompare(b.groupPath!));
}

function psfTemplateTreeItem(psfTemplate: PsfTemplate, role: string, path: string): TreeViewItem {
    return {
        id: psfTemplate.id!.toString(),
        object: psfTemplate,
        name: psfTemplate.name,
        groupPath: path,
        groupRole: role,
        type: ResourceType.PERFORMANCE_SHAPING_FACTORS,
        hasChildren: false
    };
}

export function makePsfTemplate(psfTemplate: any): PsfTemplate {
    let psft: PsfTemplate = {} as PsfTemplate;
    psft.id = psfTemplate.id;
    psft.name = psfTemplate.name;
    psft.description = psfTemplate.description;
    psft.presentation = psfTemplate.presentation;
    psft.group = psfTemplate.group ? makeGroup(psfTemplate.group) : new Group();
    psft.psfTemplateItems = psfTemplate.psfTemplateItems;
    return psft;
}

export function getPsfTemplatePathToRootAsArrayOfString(psfTemplate: PsfTemplate): Array<BreadCrumbsProp> {
    let path: Array<BreadCrumbsProp> = [];
    if (psfTemplate.id) {
        path = getPathToRootAsArrayOfString(psfTemplate.group);
    }

    return [...path, {
        id: psfTemplate.id,
        label: psfTemplate.name && psfTemplate.name !== '' ? psfTemplate.name : 'New Psf Template',
        type: ResourceType.PERFORMANCE_SHAPING_FACTORS}];
}

export function psfCategoryTreeItems(categories: PsfCategory[]|undefined): TreeViewItem[] {
    const treeItems: TreeViewItem[] = [];
    if (categories && categories.length > 0) {
        categories.forEach((category, index) => {
            const categorySeries = category.path.split('::');
            let path = '';
            let treeViewItem = {} as TreeViewItem|undefined;
            categorySeries.forEach((subCat) => {
                path = path === '' ? subCat : path + '::' + subCat;
                if (!treeViewItem?.name) {
                    treeViewItem = treeItems.find((item) => item.name === subCat);
                    if (treeViewItem === undefined) {
                        treeViewItem = psfCategoryTreeItem(index, subCat, path);
                        treeItems.push(treeViewItem);
                    }
                } else {
                    if (!treeViewItem.children) treeViewItem.children = [] as TreeViewItem[];
                    let children = treeViewItem.children;
                    treeViewItem = children.find((child) => child.name === subCat);
                    if (treeViewItem === undefined) {
                        treeViewItem = psfCategoryTreeItem(index, subCat, path);
                        children.push(treeViewItem);
                    }
                }
            })
        });
    }
    return treeItems;
}

function psfCategoryTreeItem(index: number, categoryName: string, path: string): TreeViewItem {
    return {
        id: index + '',
        name: categoryName,
        groupPath: path,
        type: ResourceType.UNKNOWN
    };
}

export function isNotEditable(psfTemplate: PsfTemplate, isSuperUser?: boolean): boolean {
    return psfTemplate.id !== null && psfTemplate.id !== undefined && !hasRightToEdit({objectType: ResourceType.PERFORMANCE_SHAPING_FACTORS, object: psfTemplate}, isSuperUser);
}

export function getCategoryName(path: string): string {
    const lastSep = path.lastIndexOf('::');
    return lastSep > 0 ? path.substring(lastSep + 2) : path;
}

export function populateCategories(psfTemplate: PsfTemplate): PsfCategory[] {
    let categories = [] as PsfCategory[];
    categories = psfTemplate?.presentation?.v1?.orderedPsfTemplpateItemsIncludingEmptyCategories
        .map((cats, index) => { return {
            index: index,
            name: getCategoryName(cats[0] as string),
            path: cats[0] as string,
            psfTemplateItems: []
        }; }) ?? [] as PsfCategory[];
    psfTemplate?.psfTemplateItems?.forEach((item) => {
        categories.find((category) => category.path === item.category)?.psfTemplateItems?.push(item);
    })
    return categories;
}

export function replaceNameInPath(path: string, oldName: string, newName: string): string {
    return path.split('::').map((name) => name === oldName ? newName : name).join('::');
}
